import { isMapUrl } from "@/lib/calendarEventPlace";
import { isHubIosShell } from "@/lib/hubShell";
import { openHrefInNewTab } from "@/lib/openExternalLink";

export type MapsProvider = "apple" | "google";

/** Text to send to map search when the display line is empty but we have a map link. */
export function mapsSearchQuery(display: string, existingHref: string | null): string {
  const text = (display || "").trim();
  if (text) return text;
  if (!existingHref) return "";
  try {
    const url = new URL(existingHref);
    for (const key of ["q", "query", "address"]) {
      const value = url.searchParams.get(key);
      if (value?.trim()) return value.trim();
    }
  } catch {
    /* ignore */
  }
  return "";
}

export function googleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function appleMapsSearchUrl(query: string): string {
  return `https://maps.apple.com/?q=${encodeURIComponent(query)}`;
}

export function appleMapsAppUrl(query: string): string {
  return `maps://?q=${encodeURIComponent(query)}`;
}

export function googleMapsAppUrl(query: string): string {
  return `comgooglemaps://?q=${encodeURIComponent(query)}`;
}

export function resolveMapsOpenHref(input: {
  provider: MapsProvider;
  display: string;
  existingHref: string | null;
}): string {
  const query = mapsSearchQuery(input.display, input.existingHref);
  const href = input.existingHref?.trim() || null;

  if (href && isMapUrl(href)) {
    const appleLink = /maps\.apple\.com/i.test(href);
    const googleLink = /google\.[^/]+\/maps|maps\.google|maps\.app\.goo\.gl|goo\.gl\/maps/i.test(href);
    if (input.provider === "apple" && appleLink) return href;
    if (input.provider === "google" && googleLink) return href;
  }

  if (!query) {
    if (href && isMapUrl(href)) return href;
    return href || "";
  }

  if (input.provider === "apple") {
    return isHubIosShell() ? appleMapsAppUrl(query) : appleMapsSearchUrl(query);
  }
  return isHubIosShell() ? googleMapsAppUrl(query) : googleMapsSearchUrl(query);
}

/** Open a maps destination from a user gesture. */
export function openMapsHref(href: string): void {
  const tip = (href || "").trim();
  if (!tip) return;

  if (/^(maps|geo|comgooglemaps):/i.test(tip)) {
    if (isHubIosShell()) {
      window.location.assign(tip);
      return;
    }
    const q = tip.match(/[?&]q=([^&]+)/i)?.[1];
    if (q) {
      openHrefInNewTab(googleMapsSearchUrl(decodeURIComponent(q)));
      return;
    }
    return;
  }

  openHrefInNewTab(tip);
}

export function openCalendarLocationInMaps(
  provider: MapsProvider,
  display: string,
  existingHref: string | null,
): void {
  const href = resolveMapsOpenHref({ provider, display, existingHref });
  if (href) openMapsHref(href);
}

export async function copyCalendarLocationText(text: string): Promise<boolean> {
  const value = text.trim();
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}
